import { Hotel } from "../../../assets/Services/wsSearch";
import { currHotelID } from "./Results";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useSearch } from "../../hooks/useSearch";
import ScrollContainer from "../../../assets/TakyonDesignSystem/components/ScrollContainer/ScrollContainer";
import imageBadgePlus from "../../../assets/img/directsearch/badge_plus.avif";
import IconStar from "../../../assets/icons/IconStar";
import { advantagesMap } from "../../../config/advantages";
import IconBadgePlus from "../../../assets/icons/IconBadgePlus";
import { currencySymbols } from "../../../assets/Utils/prices";
import { getDisplayPrice, getOTADeltaPrice } from "./utils";

interface MapInfoHotelProps {
  hotels: Hotel[];
  currIndex: number;
  onHotelClick: (hotel: Hotel, slug?: string) => void;
  setCurrIndex: (index: number) => void;
}

export default function MapInfoHotel({
  hotels,
  currIndex,
  onHotelClick,
  setCurrIndex,
}: MapInfoHotelProps) {
  const { t } = useTranslation();

  const { searchParameters } = useSearch();
  const hotelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [isScrollingHorizontally, setIsScrollingHorizontally] = useState(false);

  useEffect(() => {
    if (
      currIndex >= 0 &&
      currIndex < hotels.length &&
      hotelRefs.current[currIndex]
    ) {
      // Scroll the item into view with smooth behavior
      hotelRefs.current[currIndex]?.scrollIntoView({
        block: "nearest",
        inline: "center",
      });
    }
  }, [currIndex, hotels.length]);

  // Calculate container height when first hotel is rendered
  useEffect(() => {
    const currHotel = hotelRefs.current[currIndex];
    if (!currHotel) return;

    if (currHotel) {
      const height = currHotel.offsetHeight;
      setContainerHeight(height);
    }
  }, [hotels, currIndex]);

  // Aggiungiamo un gestore di eventi touch direttamente al DOM con passive: false
  useEffect(() => {
    // Funzione per prevenire lo scroll della pagina durante lo swipe verticale
    const preventDefaultForVerticalSwipe = (e: TouchEvent) => {
      if (!touchStartY || !touchStartX) return;

      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const deltaY = Math.abs(touchY - touchStartY);
      const deltaX = Math.abs(touchX - touchStartX);

      // Se il movimento è principalmente verticale, preveniamo il comportamento predefinito
      if (deltaY > deltaX && deltaY > 10) {
        e.preventDefault();
      }
    };

    // Aggiungiamo l'event listener al documento con passive: false
    document.addEventListener("touchmove", preventDefaultForVerticalSwipe, {
      passive: false,
    });

    // Puliamo l'event listener quando il componente viene smontato
    return () => {
      document.removeEventListener("touchmove", preventDefaultForVerticalSwipe);
    };
  }, [touchStartY, touchStartX]);

  // Function to determine which hotel is most visible in the viewport
  const determineVisibleHotel = () => {
    if (!containerRef.current || hotels.length === 0) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestHotel = 0;
    let minDistance = Number.MAX_VALUE;

    hotelRefs.current.forEach((ref, index) => {
      if (ref) {
        const hotelRect = ref.getBoundingClientRect();
        const hotelCenter = hotelRect.left + hotelRect.width / 2;
        const distance = Math.abs(containerCenter - hotelCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestHotel = index;
        }
      }
    });

    if (currIndex !== closestHotel) {
      setCurrIndex(closestHotel);
      currHotelID.value = hotels[closestHotel]._id;
    }
  };

  // Set up scroll event listener to detect when scrolling stops
  useEffect(() => {
    const scrollContainer = containerRef.current;
    if (!scrollContainer) return;

    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Check which hotel is visible after scrolling stops (300ms delay)
      scrollTimeout = setTimeout(() => {
        determineVisibleHotel();
      }, 100);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [hotels, currIndex]);

  // Manual touch event handlers for swipe detection
  const handleTouchStart = (e: React.TouchEvent) => {
    // Reset horizontal scrolling flag at the start of a new touch
    setIsScrollingHorizontally(false);
    setTouchStartY(e.touches[0].clientY);
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX === null || touchStartY === null) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = Math.abs(currentX - touchStartX);
    const deltaY = Math.abs(currentY - touchStartY);

    // Detect if this is primarily a horizontal scroll
    // Once we've determined it's horizontal, keep that state for the duration of the touch
    if (!isScrollingHorizontally && deltaX > deltaY && deltaX > 10) {
      setIsScrollingHorizontally(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY === null || touchStartX === null) return;

    // If we detected horizontal scrolling during this touch, ignore the vertical gesture
    if (isScrollingHorizontally) {
      setTouchStartY(null);
      setTouchStartX(null);
      return;
    }

    const touchEndY = e.changedTouches[0].clientY;
    const touchEndX = e.changedTouches[0].clientX;
    const deltaY = touchEndY - touchStartY;
    const deltaX = Math.abs(touchEndX - touchStartX);

    // Ensure this is primarily a vertical gesture
    const SWIPE_THRESHOLD = 50;
    const HORIZONTAL_TOLERANCE = 30; // Maximum allowed horizontal movement

    // Only process the vertical swipe if horizontal movement is minimal
    if (deltaX <= HORIZONTAL_TOLERANCE) {
      // Swipe down detection
      if (deltaY > SWIPE_THRESHOLD && !isCollapsed) {
        setIsCollapsed(true);
        setScrollEnabled(false);
      }
      // Swipe up detection
      else if (deltaY < -SWIPE_THRESHOLD && isCollapsed) {
        setIsCollapsed(false);
        setScrollEnabled(true);
      }
    }

    // Reset touch coordinates
    setTouchStartY(null);
    setTouchStartX(null);
  };

  // Calculate the height to show when collapsed (50px from the top)
  const collapsedTranslateY = containerHeight - 50;

  return (
    <motion.div
      animate={{
        y: isCollapsed ? collapsedTranslateY : 0,
      }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      // className="w-100"
      className="position-fixed bottom-0 w100 pe-none"
      ref={cardContainerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      <ScrollContainer>
        <div
          ref={containerRef}
          className={`flex gap-1 px-4 overflow-x-scroll items-end snap-x ${
            !scrollEnabled ? "overflow-hidden" : ""
          }`}
          onScroll={() => {
            // This helps trigger the scroll event more reliably
            // The main logic is in the useEffect with the debounced handler
          }}
          style={{
            pointerEvents: "none",
          }}
        >
          {hotels.map((item, key) => {
            let advantages = item.advantagesOnDirectWebsite;
            if (advantages) {
              advantages = advantages.slice(0, 3); // Keep only the first three elements
            }

            const isExtraDiscountApplied =
              item?.isLoyaltyEnabled &&
              item?.isLoyaltyEnabledAdmin &&
              item?.loyatyDiscountPercentage;

            const highlightedAdvantage = item.advantagesOnDirectWebsite?.[0];

            const HighlightedAdvantageIcon =
              advantagesMap[highlightedAdvantage as keyof typeof advantagesMap]
                ?.icon;
            const highlightedAdvantageLabel =
              advantagesMap[highlightedAdvantage as keyof typeof advantagesMap]
                ?.label;

            return (
              <div
                key={item._id}
                ref={(el) => (hotelRefs.current[key] = el)}
                className="m-2 rounded-[1.25rem] shadow w-[calc(100vw-2rem)] snap-center min-h-28 pointer-events-auto bg-white shrink-0 p-3 flex gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onHotelClick(item, item.slug);
                }}
              >
                <div
                  className="rounded-2xl box-border ring-4 ring-white ring-inset shadow-lg bg-cover bg-center bg-[#efefef] w-[6.25rem] sm:w-32 md:w-36 self-stretch shrink-0"
                  style={{
                    backgroundImage: `url(${
                      item.images?.[0]?.replace("square60", "square600") || ""
                    })`,
                  }}
                />
                <div className="flex-1">
                  <header className="flex justify-between">
                    <div className="relative">
                      <h3 className="text-base font-medium mb-0 whitespace-normal break-words line-clamp-2 pr-6">
                        {item.name}
                      </h3>
                      {item.isActive && (
                        <img
                          src={imageBadgePlus}
                          role="presentation"
                          className="absolute top-0 right-0 size-5 shrink-0"
                          alt="plus"
                        />
                      )}
                    </div>
                    <div className="hidden sm:flex items-center gap-1 shrink-0">
                      {item.isActive && <IconBadgePlus className="size-3.5" />}
                      <span className="text-black text-xs font-medium">
                        {t("ds.official_website")}
                      </span>
                    </div>
                    {/* <ButtonBookmark hotelId={item._id} /> */}
                  </header>
                  <main className="flex flex-col">
                    <p className="text-sm font-medium text-[#747474] text-left gap-1 flex items-center flex-wrap leading-none">
                      {!!item.reviewScore && !!item.reviewCount && (
                        <>
                          <span>{item.reviewScore}</span>
                          <IconStar className="size-2.5" />
                          <span className="hidden lg:hidden sm:block xl:block">
                            {t(
                              `ds.rating_${
                                item.reviewScore.toString().split(".")[0]
                              }`,
                            )}
                          </span>
                          <span className="text-[#A2A2A2] block lg:block sm:hidden xl:hidden">
                            {`(${new Intl.NumberFormat(
                              searchParameters.language ?? "en-US",
                            ).format(item.reviewCount)})`}
                          </span>
                          <span className="text-[#A2A2A2] hidden lg:hidden sm:block xl:block">
                            {`(${new Intl.NumberFormat(
                              searchParameters.language ?? "en-US",
                            ).format(item.reviewCount)} ${t("ds.review", {
                              count: item.reviewCount,
                            })})`}
                          </span>
                        </>
                      )}
                      {!(!!item.reviewScore && !!item.reviewCount) && (
                        <span>{t("ds.no_reviews_yet")}</span>
                      )}
                      {item.stars && Number(item.stars) !== 0 && (
                        <span>
                          {`  Hotel ${item.stars} ${t("ds.star", {
                            count: Number(item.stars),
                          })}`}
                        </span>
                      )}
                    </p>
                    <p className="text-sm font-medium text-[#747474] text-left hidden sm:block">
                      {`${item.address}, ${item.city}`}
                    </p>
                    {highlightedAdvantage && (
                      <div className="flex items-center gap-1 mt-1 sm:bg-[#F8F8F8] sm:px-2 sm:py-1 rounded-xl w-fit">
                        {HighlightedAdvantageIcon && (
                          <HighlightedAdvantageIcon className="size-3" />
                        )}
                        <span className="text-xs font-medium">
                          {t(highlightedAdvantageLabel)}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col items-end justify-center gap-1">
                      {item.availability &&
                      item.availability.availabilityInfo.available ? (
                        <div className="flex justify-between items-end gap-0.5">
                          <p className="text-xs font-medium">{t("ds.from2")}</p>
                          <p className="font-medium text-xl translate-y-1">
                            {getDisplayPrice({
                              availability: item.availability,
                              deltaBooking: item.deltaPriceBookingCom,
                              deltaHotels: item.deltaPriceHotelsCom,
                              lang: searchParameters.language,
                            })}
                          </p>
                        </div>
                      ) : (
                        <div className="flex rounded-lg px-2 py-1 justify-center items-center bg-[#E8E8E8] mt-2">
                          <p className="text-xs font-medium text-[#747474]">
                            {t("ds.no_availability")}
                          </p>
                        </div>
                      )}
                      {item.availability?.availabilityInfo.available ? (
                        <div className="flex items-center justify-end w-full">
                          <p className="text-xs text-[#EF4444] font-medium">
                            {getOTADeltaPrice({
                              availability: item.availability,
                              deltaBooking: item.deltaPriceBookingCom,
                              deltaHotels: item.deltaPriceHotelsCom,
                              lang: searchParameters.language,
                            })}
                          </p>
                        </div>
                      ) : null}
                      {isExtraDiscountApplied && item.isActive && (
                        <div className="flex items-center justify-center rounded-lg px-1.5 py-1 min-h-6 bg-black text-center">
                          <p className="text-xs text-white font-medium text-wrap">
                            {t("ds.loyalty_discount_with_code", {
                              percentage: item?.loyatyDiscountPercentage,
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  </main>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollContainer>
    </motion.div>
  );
}
